import { createSelector } from "reselect";
import { FeatureToggleSelectors, statusSuccess, IntlSelectors, uninitState, successState } from "@grandvisionhq/state";
import { isDefined } from "@grandvisionhq/utils";
import { isNewPrescriptionAndUpload } from "../../../../utils/utils/is-new-prescription-upload.js";
import { isPupillaryDistance, isAxis, isCylinder } from "../../../../utils/utils/parameter-type-guards.js";
import { getOpticalParameters } from "../../../bundles/state/bundles/selectors.internal.js";
import { getConfigurator } from "../../../configurator/state/configurator/selectors.internal.js";
import { RX_SCOPE, PD_SCOPE } from "../../../state/constants.js";
import { convertToLensTypeInfo, convertToPackageInfo, convertToLensAddOnInfo } from "../../../state/helpers.js";
import { lookupParentLensType, getPackage, lookupParentAddOn } from "../../../products/state/products/selectors.internal.js";
import { isParameterValueValid } from "./helpers.js";
import { getConfiguration, getLensTypeSku, getPackageSku, getAddOnSku, getPrescription as getPrescription$1 } from "./selectors.internal.js";
const getPrescriptionType = createSelector(
  getConfiguration,
  (c) => c?.prescriptionType
);
const getIsPupillaryDistanceFileOptional = createSelector(
  FeatureToggleSelectors.getFeatureToggles,
  (c) => c?.isPupillaryDistanceFileOptional
);
const getIsRxAxisCylinderOptional = createSelector(
  FeatureToggleSelectors.getFeatureToggles,
  (c) => c?.isRxAxisCylinderOptional
);
const getValidationParameters = createSelector(
  getOpticalParameters,
  getPrescriptionType,
  getIsPupillaryDistanceFileOptional,
  getIsRxAxisCylinderOptional,
  (parameters, prescriptionType, isPDFileOptional, isAxisCylinderOptional) => {
    if (!statusSuccess(parameters)) {
      return void 0;
    }
    const isOptionalParam = (param) => {
      if (isPupillaryDistance(param) && isPDFileOptional && isNewPrescriptionAndUpload(prescriptionType)) {
        return true;
      }
      if (isAxisCylinderOptional && (isAxis(param) || isCylinder(param))) {
        return true;
      }
      return false;
    };
    return parameters.result.map((param) => ({
      ...param,
      isOptional: isOptionalParam(param)
    }));
  }
);
const getFormValidationParameters = createSelector(
  (s) => s,
  getConfigurator,
  (state, config) => {
    if (!config?.frameSku) {
      return void 0;
    }
    const lensTypeSku = getLensTypeSku(state, { frameSku: config.frameSku });
    if (!lensTypeSku) {
      return void 0;
    }
    return getValidationParameters(state, { frameSku: config.frameSku, lensTypeSku });
  }
);
const getSelectedLensType = createSelector(
  getLensTypeSku,
  IntlSelectors.getLocale,
  (s) => s,
  (lensTypeSku, locale, state) => {
    if (!lensTypeSku || !locale) {
      return void 0;
    }
    const lensTypeProduct = lookupParentLensType(state, { lensTypeSku });
    if (!lensTypeProduct) {
      return void 0;
    }
    return convertToLensTypeInfo(lensTypeProduct, lensTypeSku, locale);
  }
);
const getSelectedPackage = createSelector(
  getPackageSku,
  IntlSelectors.getLocale,
  (s) => s,
  (packageSku, locale, state) => {
    if (!packageSku || !locale) {
      return void 0;
    }
    const packageProduct = getPackage(state, {
      packageSku
    });
    if (!packageProduct) {
      return void 0;
    }
    return convertToPackageInfo(packageProduct, packageSku, locale);
  }
);
const getSelectedAddOn = createSelector(
  getAddOnSku,
  IntlSelectors.getLocale,
  (s) => s,
  (addOnSku, locale, state) => {
    if (!addOnSku || !locale) {
      return;
    }
    const addOnProduct = lookupParentAddOn(state, {
      addOnSku
    });
    if (!addOnProduct) {
      return void 0;
    }
    return convertToLensAddOnInfo(addOnProduct, addOnSku, locale);
  }
);
const getPrescription = createSelector(
  getPrescription$1,
  getLensTypeSku,
  getOpticalParameters,
  (prescription, lensTypeSku, parameters) => {
    if (!lensTypeSku) {
      return uninitState();
    }
    if (!statusSuccess(parameters)) {
      return parameters;
    }
    return successState({
      parameters: parameters.result,
      ...prescription,
      uploadReferences: prescription.uploadReferences || {}
    });
  }
);
const getAdditionalPrescriptionInfoFormState = createSelector(
  getPrescription$1,
  (prescription) => prescription.additionalInfo ?? {}
);
const diff = (a, b) => new Set([...b].filter((i) => !a.has(i)));
const getManualPrescriptionValidationState = createSelector(
  getPrescription$1,
  getLensTypeSku,
  getValidationParameters,
  (prescription, lensTypeSku, parameters) => {
    if (!lensTypeSku) {
      return void 0;
    }
    if (!parameters) {
      return void 0;
    }
    const parameterNames = new Set(parameters.map((p) => p.name));
    const leftHasExtra = diff(parameterNames, Object.keys(prescription.left)).size > 0;
    const rightHasExtra = diff(parameterNames, Object.keys(prescription.right)).size > 0;
    const initialValidationState = {
      isValid: !leftHasExtra && !rightHasExtra,
      left: {
        isValid: !leftHasExtra
      },
      right: {
        isValid: !rightHasExtra
      }
    };
    const isAxisCylinderValid = (paramName, axisValue, cylinderValue) => {
      const axisFilled = isDefined(axisValue) && axisValue !== 0;
      const cylinderFilled = isDefined(cylinderValue) && cylinderValue !== 0;
      if (paramName === "axis" && !axisFilled && cylinderFilled) {
        return false;
      }
      if (paramName === "cylinder" && !cylinderFilled && axisFilled) {
        return false;
      }
      return true;
    };
    return parameters.reduce((acc, param) => {
      const leftValue = prescription.left[param.name];
      const rightValue = prescription.right[param.name];
      const isLeftValid = isParameterValueValid(param, leftValue) && isAxisCylinderValid(param.name, prescription.left.axis, prescription.left.cylinder);
      const isRightValid = isParameterValueValid(param, rightValue) && isAxisCylinderValid(param.name, prescription.right.axis, prescription.right.cylinder);
      const left = {
        ...acc.left,
        isValid: acc.left.isValid && isLeftValid,
        [param.name]: isLeftValid
      };
      const right = {
        ...acc.right,
        isValid: acc.right.isValid && isRightValid,
        [param.name]: isRightValid
      };
      return {
        isValid: acc.isValid && left.isValid && right.isValid,
        left,
        right
      };
    }, initialValidationState);
  }
);
const getPdUploadValidationRequirement = createSelector(
  getPrescriptionType,
  getPrescription$1,
  getIsPupillaryDistanceFileOptional,
  (prescriptionType, prescription, isPDFileOptional) => {
    if (isNewPrescriptionAndUpload(prescriptionType) && isPDFileOptional) {
      return {
        isRequired: !isDefined(prescription?.left.pupillaryDistance) || !isDefined(prescription?.right.pupillaryDistance)
      };
    }
    return { isRequired: true };
  }
);
const getPrescriptionUploadValidationState = createSelector(
  getPrescription$1,
  getLensTypeSku,
  getPdUploadValidationRequirement,
  (prescription, lensTypeSku, pdUploadValidationRequirement) => {
    if (!lensTypeSku) {
      return void 0;
    }
    const { uploadReferences } = prescription;
    const hasUploadedRx = !!(uploadReferences || {})[RX_SCOPE]?.storageToken;
    const hasUploadedPd = !!(uploadReferences || {})[PD_SCOPE]?.storageToken;
    const isRxValid = hasUploadedRx;
    const isPdValid = !pdUploadValidationRequirement.isRequired || hasUploadedPd;
    return {
      isValid: isRxValid && isPdValid,
      [RX_SCOPE]: {
        isValid: isRxValid
      },
      [PD_SCOPE]: {
        isValid: isPdValid
      }
    };
  }
);
const getVerificationAllowed = createSelector(
  getPrescription$1,
  (c) => c?.verificationAllowed ?? false
);
export {
  getAdditionalPrescriptionInfoFormState,
  getFormValidationParameters,
  getIsPupillaryDistanceFileOptional,
  getIsRxAxisCylinderOptional,
  getManualPrescriptionValidationState,
  getPdUploadValidationRequirement,
  getPrescription,
  getPrescriptionType,
  getPrescriptionUploadValidationState,
  getSelectedAddOn,
  getSelectedLensType,
  getSelectedPackage,
  getValidationParameters,
  getVerificationAllowed
};
//# sourceMappingURL=selectors.js.map
